import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { get, merge } from 'lodash';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import {
  TestSetupComponent,
  ITestSetupEntry,
  BaseSetupTab,
  TestSetupTabsComponent,
  SaveTemplateComponent,
  TemplateService
} from '@nrwl/test-setup';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { TruespeedSetupService } from './truespeed-setup.service';
import { TruespeedSetupBasicTabComponent } from './tabs/truespeed-setup-basic-tab/truespeed-setup-basic-tab.component';
import { TruespeedSetupAdvanceTabComponent } from './tabs/truespeed-setup-advance-tab/truespeed-setup-advance-tab.component';
import {
  DialogService,
  HelpersService,
  NotificationService,
  RunTypeValue,
  TestListService,
  TestMode,
  TestTypeValue,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { TemplateTabsComponent } from '@nrwl/template-management';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TruespeedConfigFieldMapping as ConfigFieldMapping } from './tabs/models/model';
@Component({
  selector: 'truespeed-setup',
  providers: [TruespeedSetupService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class TruespeedTestSetupComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy, ITestSetupEntry
{
  @ViewChild(TestSetupTabsComponent, { static: true }) testSetupTabRef: TestSetupTabsComponent;
  templateDialogRef: MatDialogRef<TemplateTabsComponent>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupConfig: any;
  agentBasedConfig: any;
  setupId: string;
  testId: string;
  mode: string;
  templateName: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  setupService: TruespeedSetupService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  activatedRouteSubscription: Subscription;
  editDeviceDialogRef: MatDialogRef<SaveTemplateComponent>;
  currentObj: any; //eslint-disable-line
  shaId: string;
  constructor(
    router: Router,
    setupService: TruespeedSetupService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly navigator: NavigationProvider,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialogService: DialogService,
    private dialog: MatDialog,
    private readonly testListService: TestListService,
    private readonly exchange: ValuesExchangeProvider,
    private helpersService: HelpersService,
    private readonly notificationService: NotificationService,
    private readonly templateService: TemplateService
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.currentObj = this;
    if (this.mode === 'new') {
      this.setupService.getControllerConfiguration();
    }
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      if (this.testId !== this.setupId) {
        const defaultParams = merge(
          {},
          this.dataId,
          { modeType: this.mode },
          JSON.parse(get(data, 'defaultParams', '{}'))
        );
        this.setupService.openTestSetup(
          this.setupId,
          this.setupConfig,
          defaultParams,
          null,
          null,
          this.agentBasedConfig
        );
        this.testId = this.setupId;
      }
    });
    this.getLicenses();
    this.getServerConfig();
    if (!this.setupService.editMode) {
      this.setupService.currentConfig.buttonName = 'START TEST';
    }
    this.templateService.closeDialog?.subscribe(status => {
      if (status && this.templateDialogRef) {
        this.templateDialogRef.close();
      }
    });
  }

  getServerConfig(): void {
    this.setupService.getServerConfig().subscribe(response => {
      this.setupService.clientToAgentInfo.serverConfigInfo = response;
      this.setupService.setServerConfigData(response?.data);
      if (
        this.setupService.editMode &&
        this.setupService.currentTab.output.testMode === TestMode.CLIENT_TO_MA
      ) {
        this.shaId = this.setupService.tabs[0].output['shaId'];
        this.setClientURLs();
      }
    });
  }

  getLicenses(): void {
    this.setupService.getLicenseData().subscribe(response => {
      this.setupService.clientToAgentInfo.licenseInfo = response;
    });
  }
  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.setupService.reOpen();
  }

  openTestRunning(testId: string): void {
    this.navigator.navigateToDashboard(['test-running'], {
      queryParams: {
        testId: testId,
        testName: this.setupService.output.alias,
        testType: TestTypeValue.TRUESPEED,
        onDemandTest: this.setupService.output.runType === RunTypeValue.ON_DEMAND,
        source: null,
        layeredSource: [],
        queryParamsHandling: 'merge',
        hideTestSetup: true, // to hide the testsetup wizard panel
        replaceUrl: true
      }
    });
  }
  downloadTestConfig(): void {
    this.setupService.onNext();
    const allTestParams = this.setupService.getAllTestParams();
    const lmapObject = this.setupService.createLMAPObject(allTestParams);
    this.setupService._downloadTestConfig(lmapObject);
  }
  saveTestConfig(): void {
    this.setupService.onNext();
    let configParams = this.setupService._getConfigParameters();
    const otherParams = {
      [ConfigFieldMapping.EXPIRATION_DATE]:
        this.setupService.output[ConfigFieldMapping.EXPIRATION_DATE],
      [ConfigFieldMapping.MAX_EXECUTION_COUNTS]:
        this.setupService.output[ConfigFieldMapping.MAX_EXECUTION_COUNTS],
      [ConfigFieldMapping.SCHEDULER]: this.setupService.output[ConfigFieldMapping.SCHEDULER]
    };
    configParams = { ...configParams, ...otherParams };
    const allTestParams = this.setupService._createTemplateFormat(
      configParams,
      this.setupService.selectedOriginator,
      this.setupService.selectedDestination
    );
    allTestParams['engine-a-info'] = this.setupService.getEngineAParams();
    allTestParams['engine-b-info'] = this.setupService.getEngineBParams();
    this.editDeviceDialogRef = this.dialog.open(SaveTemplateComponent, {
      width: '70rem',
      disableClose: true,
      data: { deviceInfo: allTestParams, editMode: true, testType: TestTypeValue.TRUESPEED }
    });
  }

  openTemplates(): void {
    const param = {
      id: 'setupService.test_templates',
      callBackService: this,
      callBackFuntion: this.openTemplatesDialog
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }
  // eslint-disable-next-line
  openTemplatesDialog(service: any): void {
    service.toggleClose();
    service.templateDialogRef = service.dialog.open(TemplateTabsComponent, {
      width: '100rem',
      minHeight: '50rem',
      height: 'calc(100vh - 20px)',
      disableClose: true,
      data: {
        filterType: 'truespeed',
        myTemplatesColumnHeaders: ['name', 'accessLevel', 'lastModified', 'selectActions'],
        sharedTemplatesColumnHeaders: [
          'name',
          'accessLevel',
          'owner',
          'lastModified',
          'selectActions'
        ]
      }
    });
  }
  toggleFinish(): void {
    if (
      this.setupService.currentTab.output?.downstream >= 500 ||
      this.setupService.currentTab.output?.upstream >= 500
    ) {
      const param = {
        id: 'setupService.truespeed_test_max_bandwidth',
        alternateTitle: ' ',
        callBackService: this,
        callBackFuntion: this.createTruespeedTest
      };
      this.dialogService.showDialog('confirm', 'body', param);
    } else {
      this.createTruespeedTest(this);
    }
  }
  // eslint-disable-next-line
  createTruespeedTest(service: any): void {
    service.setupService.onNext();
    const allTestParams = service.setupService.getAllTestParams();
    this.setupService.openTestStatusDialog(
      service.setupService.editMode ? 'Updating truespeed' : 'Creating truespeed',
      service.setupService.editMode ? 'Update' : 'Add'
    );
    const lmapObject = service.setupService.createLMAPObject(allTestParams);
    // lmapObject complete payload
    if (service.setupService.output.testMode === TestMode.CLIENT_TO_MA) {
      if (service.setupService.editMode) {
        lmapObject['runlater'] = 'yes';
        service.subscription = service.setupService
          ._updateControllerTest({
            opt: { id: service.shaId, apply: 'next-run' },
            config: lmapObject
          })
          .subscribe(
            response => {
              if (response.status === 'success') {
                service.notificationService.onSuccess('Configuration was updated successfully');
                service.setupService.dialogRef.close();
                service.testSetupTabRef.toggleTabChange(0);
              }
            },
            err => {
              service.setupService.dialogRef.close();
              this.notificationService.onFail('Configuration was not updated successfully');
            }
          );
      } else {
        service.subscription = service.setupService._createControllerTest(lmapObject).subscribe(
          response => {
            service.testListService.updateTestResponse();
            service.generateUrls(response);
            service.setupService.dialogRef.close();
            service.exchange.notifyListeners('new_test', response);
            service.testSetupTabRef.toggleTabChange(0);
          },
          error => {
            const message = service.setupService.editMode
              ? 'Failed to update truespeed test'
              : 'Failed to create truespeed test';
            service.notificationService.onFail(message);
            service.setupService.dialogRef.close();
          }
        );
      }
    } else {
      if (service.setupService.editMode) {
        service.subscription = service.setupService
          ._updateControllerTest({
            opt: { id: this.setupService.defaultParamsById['scheduleId'], apply: 'next-run' },
            config: lmapObject
          })
          .subscribe(
            response => {
              const message = 'Truespeed test has been updated';
              this.notificationService.onSuccess(message);
              this.testListService.updateTestResponse();
              this.setupService.dialogRef.close();
              this.toggleClose();
              this.exchange.notifyListeners('new_test', response);
            },
            err => {
              const message = 'Failed to update truespeed test';
              this.notificationService.onFail(message);
              this.setupService.dialogRef.close();
              this.toggleClose();
            }
          );
      } else {
        service.subscription = service.setupService._saveControllerTest(lmapObject).subscribe(
          response => {
            const message = 'Truespeed test has been created';
            this.notificationService.onSuccess(message);
            this.testListService.updateTestResponse();
            this.setupService.dialogRef.close();
            this.toggleClose();
            this.exchange.notifyListeners('new_test', response);
            if (this.setupService.output?.runType === RunTypeValue.ON_DEMAND) {
              this.openTestRunning(response.data.id);
            }
          },
          error => {
            const message = 'Failed to create truespeed test';
            service.notificationService.onFail(message);
            service.setupService.dialogRef.close();
            this.toggleClose();
          }
        );
      }
    }
  }

  setClientURLs(): void {
    this.setupService.clientToAgentInfo.dmzServerUrl = null;
    this.setupService.clientToAgentInfo.dmzServerUrlAdvanced = null;
    this.setupService.clientToAgentInfo.secondaryTestURL = null;
    this.setupService.clientToAgentInfo.secondaryTestURLAdvanced = null;
    const dmzUrl = this.helpersService.createPublicAccessURL(
      this.setupService.clientToAgentInfo.serverConfigInfo.data,
      this.shaId
    );
    this.setActiveLicense();
    const vTAUrl = this.setupService.generateSecondaryTestURL(
      this.setupService.clientToAgentInfo.serverConfigInfo.data,
      this.shaId
    );
    this.setupService.clientToAgentInfo.dmzURL = dmzUrl;
    this.setupService.clientToAgentInfo.vtaURL = vTAUrl;
    if (dmzUrl) {
      this.setupService.clientToAgentInfo.dmzServerUrl = `${dmzUrl}&type=${this.setupService.clientToAgentInfo.activeLicense}`;
      this.setupService.clientToAgentInfo.dmzServerUrlAdvanced = `${dmzUrl}&type=fat-engine`;
    }
    if (vTAUrl) {
      this.setupService.clientToAgentInfo.secondaryTestURL = `${vTAUrl}&type=${this.setupService.clientToAgentInfo.activeLicense}`;
      this.setupService.clientToAgentInfo.secondaryTestURLAdvanced = `${vTAUrl}&type=fat-engine`;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  generateUrls(response: any): void {
    if (response && response.status === 'success') {
      this.setupService.editMode = true;
      if (response.data && response.data.sha) {
        this.shaId = response.data.sha;
        this.setupService.tabs[0].output['shaId'] = this.shaId;
        this.setClientURLs();
        if (
          this.setupService.clientToAgentInfo.secondaryTestURL !== null ||
          this.setupService.clientToAgentInfo.dmzServerUrl !== null
        ) {
          this.notificationService.onSuccess(
            "Test has been added.To start test,copy a URL and paste inside the PC's browser"
          );
          this.setupService.currentConfig.buttonName = 'UPDATE';
        }
        if (
          this.setupService.clientToAgentInfo.secondaryTestURL === null &&
          this.setupService.clientToAgentInfo.dmzServerUrl === null
        ) {
          this.notificationService.onSuccess(
            'The test was successfully created. However, The test URLs were not generated because the chosen agent does not have public access enabled, or a public access server was not set under System settings->DMZ Server.'
          );
        }
      } else {
        this.notificationService.onSuccess(
          'Test created successfully but SHA not found in the response'
        );
      }
    }
  }

  setActiveLicense(): void {
    let thinClientobj = null;
    let fatClientobj = null;
    const isQtDevice =
      this.setupService.selectedOriginator.deviceData.model.model.indexOf('QT') !== -1 ||
      this.setupService.selectedOriginator.deviceData.model.model.indexOf('NSC') !== -1 ||
      this.setupService.selectedOriginator.deviceData.model.model.indexOf('MAP-2100') !== -1;
    if (this.setupService.clientToAgentInfo.licenseInfo) {
      thinClientobj = this.setupService.clientToAgentInfo.licenseInfo.find(
        o => o.featureName === 'EAX-TRUESPEED-PC-TC'
      );
      fatClientobj = this.setupService.clientToAgentInfo.licenseInfo.find(
        o => o.featureName === 'EAX-TS-PC-CLIENT'
      );
      if (thinClientobj && fatClientobj && !isQtDevice) {
        this.setupService.clientToAgentInfo.activeLicense = 'tf';
      } else if (thinClientobj && !isQtDevice) {
        this.setupService.clientToAgentInfo.activeLicense = 'thin-engine';
      } else if (fatClientobj) {
        this.setupService.clientToAgentInfo.activeLicense = 'fat-engine';
      }
    }
  }
  getSetupService(): TruespeedSetupService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'truespeed.setup.html':
        return TruespeedSetupBasicTabComponent;
      case 'truespeed.advanced.html':
        return TruespeedSetupAdvanceTabComponent;
      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }
}
